import { useState, useCallback, useMemo } from 'react';
import type { CountingProblem } from '../types/counting';
import { countingApi } from '../api/countingApi';

/**
 * The Counting practice session (feature 028).
 *
 * Counting was the only one of the three practice surfaces with no session hook: `/problems` has
 * `useProblemSession`, module practice has `useModulePracticeSession`, and Counting kept its state
 * inline across a 576-line page. This is that hook, extracted so the rebuilt page composes the
 * table frame and owns no session state of its own.
 *
 * It is deliberately NOT a shared session type. The table-session architecture note is explicit
 * that a new surface gets its own hook and its own API client, and that widening the session hook
 * into a shared interface would couple every surface's vocabulary together for good. What is
 * shared is the frame's narrow props, not this.
 *
 * **Grading is client-side, and that is not an oversight to fix here.** `CountingProblem` ships
 * `correctAnswer` to the browser — a counting question reveals its answer at the verdict anyway —
 * and the pre-028 page compared trimmed strings. The API call that follows records answer history
 * (feature 019) and is best-effort: it never blocks the UI and never decides the verdict. The
 * comparison below is byte-identical to the one it replaces, because answer parity with the
 * previous page is an acceptance criterion. Do not "improve" it with case folding or whitespace
 * normalisation — that would silently change what counts as a right answer.
 */

export interface CountingResult {
  isCorrect: boolean;
  correctAnswer: string;
  userAnswer: string;
}

export interface CountingScore {
  correct: number;
  incorrect: number;
  skipped: number;
}

export interface UseCountingSessionReturn {
  currentProblem: CountingProblem | undefined;
  currentIndex: number;
  total: number;
  score: CountingScore;
  /** correct − incorrect. Skips do not move it. */
  netScore: number;
  /** Fraction correct of those GRADED, or null when nothing has been graded yet. */
  accuracy: number | null;
  /** correct + incorrect — skips excluded, since a skip is seen-but-ungraded. */
  answered: number;
  lastResult: CountingResult | null;
  showingResult: boolean;
  isFinished: boolean;
  submit: (answer: string) => void;
  skip: () => void;
  next: () => void;
  finish: () => void;
}

export function useCountingSession(problems: CountingProblem[]): UseCountingSessionReturn {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState<CountingScore>({ correct: 0, incorrect: 0, skipped: 0 });
  const [lastResult, setLastResult] = useState<CountingResult | null>(null);
  const [showingResult, setShowingResult] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  const currentProblem = problems[currentIndex];
  const total = problems.length;

  const answered = score.correct + score.incorrect;
  const netScore = score.correct - score.incorrect;

  // null rather than 0/0 so the Session Summary scorecard can OMIT the tile instead of
  // fabricating a zero. An omission is the truth when nothing has been graded; a zero is a
  // measurement, and the scorecard can only tell them apart if this says which it has.
  const accuracy = answered === 0 ? null : score.correct / answered;

  const submit = useCallback((answer: string) => {
    // The read-only contract, enforced at the source rather than in the page that draws the
    // pads: while a recorded outcome is on screen nothing may be re-graded, which is the only
    // reason a verdict cannot be walked over and the score moved twice.
    if (showingResult) return;
    const problem = problems[currentIndex];
    if (!problem) return;

    const isCorrect = answer.trim() === problem.correctAnswer.trim();

    setLastResult({ isCorrect, correctAnswer: problem.correctAnswer, userAnswer: answer });
    setShowingResult(true);
    setScore(prev => ({
      ...prev,
      correct: prev.correct + (isCorrect ? 1 : 0),
      incorrect: prev.incorrect + (isCorrect ? 0 : 1),
    }));

    // Feature 019 — record the hand as seen so it is not re-served. Best-effort by design:
    // a learner mid-session must never be blocked by a history write.
    void countingApi.submitCountingAnswer(problem.tableResultId, answer, isCorrect).catch(() => {});
  }, [problems, currentIndex, showingResult]);

  const next = useCallback(() => {
    if (currentIndex + 1 >= problems.length) {
      setIsFinished(true);
      return;
    }
    setCurrentIndex(prev => prev + 1);
    setLastResult(null);
    setShowingResult(false);
  }, [problems.length, currentIndex]);

  const skip = useCallback(() => {
    // Same guard as submit: a hand already graded cannot then be skipped.
    if (showingResult) return;
    const problem = problems[currentIndex];
    if (problem) {
      void countingApi.skipCountingProblem(problem.tableResultId).catch(() => {});
    }
    // A skip is seen-but-ungraded: it advances pool position and suppresses the hand from
    // future sessions, but stays out of accuracy and out of the score.
    setScore(prev => ({ ...prev, skipped: prev.skipped + 1 }));
    next();
  }, [problems, currentIndex, showingResult, next]);

  /** Leave early — the tally so far is what the scorecard receives. */
  const finish = useCallback(() => {
    setIsFinished(true);
  }, []);

  return useMemo(() => ({
    currentProblem,
    currentIndex,
    total,
    score,
    netScore,
    accuracy,
    answered,
    lastResult,
    showingResult,
    isFinished,
    submit,
    skip,
    next,
    finish,
  }), [currentProblem, currentIndex, total, score, netScore, accuracy, answered,
       lastResult, showingResult, isFinished, submit, skip, next, finish]);
}
